/**
 * Medical Prescription Print Utility
 * Generates an authentic clinical Rx letterhead matching the hospital prescription format:
 * - Top Left: Hospital Name (Coral/Orange), Slogan, Doctor Info, Address, Contact (Phone, Email, Web)
 * - Top Right: Medical Stethoscope + Heart Vector Logo (or custom uploaded logo)
 * - Patient Line: Name ....... Age: ... Sex: ... Date: ...
 * - Left Rx symbol: Prominent bold orange ℞
 * - Prescription Body: Diagnosis, Complaints, Vitals, Structured Medication Table or ruled prescription pad lines
 * - Doctor Signature & Dispensary Seal
 * - Bottom Edge: Iconic Dual-Color Stripes (Top Orange 8px, Bottom Yellow 18px)
 * - Pre-printed pad mode: Option to hide the header section to print on pre-printed stationery
 */

// Blank defaults: the letterhead prints only the clinic's own details, never
// invented ones (these go on patients' medical documents).
export const DEFAULT_PRESCRIPTION_HEADER = {
  hospital_name: "",
  slogan: "",
  address: "",
  phone: "",
  email: "",
  website: "",
  logo_type: "default", // 'default' | 'custom'
  logo_url: "",
  default_hide_header: false
};

export function getPrescriptionHeaderSettings() {
  try {
    const cached = localStorage.getItem("org_prescription_header");
    if (cached) {
      const parsed = JSON.parse(cached);
      return { ...DEFAULT_PRESCRIPTION_HEADER, ...parsed };
    }
  } catch (e) {
    console.error("Failed to read cached prescription header settings", e);
  }
  return { ...DEFAULT_PRESCRIPTION_HEADER };
}

export function savePrescriptionHeaderSettingsToCache(settings) {
  try {
    localStorage.setItem("org_prescription_header", JSON.stringify(settings));
  } catch (e) {
    console.error("Failed to cache prescription header settings", e);
  }
}

export const THEME_PALETTES = {
  default: {
    name: "Serene Focus",
    primary: "#4B45A3",
    primaryDark: "#322E6E",
    primaryLight: "#EEF2FF",
    primaryBorder: "#C7D2FE",
    accent: "#3C9E95",
    stripe1: "#4B45A3",
    stripe2: "#57C7BC",
  },
  dark: {
    name: "Dark Mode",
    primary: "#1e293b",
    primaryDark: "#0f172a",
    primaryLight: "#f1f5f9",
    primaryBorder: "#cbd5e1",
    accent: "#475569",
    stripe1: "#1e293b",
    stripe2: "#94a3b8",
  },
  mindful: {
    name: "Mindful Clarity",
    primary: "#0d9488",
    primaryDark: "#0f766e",
    primaryLight: "#f0fdfa",
    primaryBorder: "#99f6e4",
    accent: "#14b8a6",
    stripe1: "#0d9488",
    stripe2: "#5eead4",
  },
  warm: {
    name: "Warm Cognition",
    primary: "#ea580c",
    primaryDark: "#9a3412",
    primaryLight: "#fff7ed",
    primaryBorder: "#fed7aa",
    accent: "#f97316",
    stripe1: "#ea580c",
    stripe2: "#facc15",
  },
  clinical: {
    name: "Clinical Pro",
    primary: "#2563eb",
    primaryDark: "#1e40af",
    primaryLight: "#eff6ff",
    primaryBorder: "#bfdbfe",
    accent: "#3b82f6",
    stripe1: "#2563eb",
    stripe2: "#60a5fa",
  }
};

export function buildPrescriptionHtml({
  consultationData,
  patient,
  doctor,
  headerConfig: customHeaderConfig,
  hideHeader: explicitHideHeader,
  triggerPrint = true,
  theme,
  // Whether this doctor's specialization can prescribe medicine at all
  // (psychologists, counselors, nutritionists, etc. cannot) - defaults true
  // so existing callers that don't pass it keep printing Rx exactly as
  // before. Only affects the header glyph/label, not any content below it.
  canPrescribeMedicine = true,
  // A non-prescriber's mental-health visit prints as a Visit Summary: the
  // patient summary plus this session's homework, agreed goals and next visit
  // (clinical/notes/visit_summary.py), never the clinical note.
  visitSummary = null,
  // Diagnosis is left off a Visit Summary unless the clinician asks for it:
  // patients hand this to employers and colleges.
  showDiagnosis = false
}) {
  const activeThemeId = (
    theme ||
    localStorage.getItem("app-theme") ||
    localStorage.getItem("theme") ||
    (typeof document !== "undefined" && document.body?.getAttribute("data-theme")) ||
    "default"
  ).toLowerCase();

  const themePalette = THEME_PALETTES[activeThemeId] || THEME_PALETTES.default;

  const auth = JSON.parse(localStorage.getItem("auth") || "{}");
  const storedConfig = getPrescriptionHeaderSettings();
  const config = { ...storedConfig, ...(customHeaderConfig || {}) };

  const hideHeader =
    typeof explicitHideHeader === "boolean"
      ? explicitHideHeader
      : Boolean(config.default_hide_header);

  const doctorSpecializationRaw = (
    doctor?.specialized ||
    doctor?.specialization ||
    doctor?.doctor_specialization ||
    doctor?.doctor?.specialized ||
    doctor?.doctor?.specialization ||
    doctor?.therapy_type ||
    auth?.specialized ||
    auth?.specialization ||
    auth?.doctor?.specialized ||
    auth?.doctor?.specialization ||
    ""
  ).toString().trim();
  // Printed in title case whatever the stored casing ("psychologists",
  // "PSYCHOLOGISTS" -> "Psychologists"); short acronyms (NICU, ENT) stay as
  // they are, and joining words stay lower case.
  const SMALL_WORDS = new Set(["and", "of", "&", "in", "the", "for"]);
  const doctorSpecialization = doctorSpecializationRaw
    .split(/\s+/)
    .map((w, i) => {
      if (/^[A-Z&]{1,4}$/.test(w)) return w;
      const lower = w.toLowerCase();
      if (i > 0 && SMALL_WORDS.has(lower)) return lower;
      return lower.charAt(0).toUpperCase() + lower.slice(1);
    })
    .join(" ");

  const doctorName =
    doctor?.name ||
    doctor?.fullname ||
    doctor?.user?.name ||
    auth?.name ||
    auth?.username ||
    (doctorSpecialization || "");

  const rawDegree = (
    doctor?.degree ||
    doctor?.doctor?.degree ||
    (doctorSpecialization ? "" : "MBBS, General Medicine")
  ).toString().trim();

  const degreeLines = rawDegree
    ? rawDegree
      .split(/\r?\n/)
      .map((l) => l.trim())
      .filter(Boolean)
    : [];

  const doctorDegree = degreeLines.join(" • ");

  const doctorReg =
    doctor?.registration_number ||
    doctor?.reg_number ||
    doctor?.doctor?.registration_number ||
    "";

  // Placeholder values older builds pre-filled into the letterhead settings -
  // an org that saved the form unchanged still has them stored. Treated as blank.
  const PLACEHOLDERS = new Set([
    "General Medicine & Outpatient Clinical Care",
    "Medical Enclave, Sector 4, Main Clinic Road, New Delhi 110029",
    "+91 98765 43210",
    "care@mindconnecthealth.com",
    "www.mindconnecthealth.com",
  ]);
  const real = (v) => (v && !PLACEHOLDERS.has(String(v).trim()) ? v : "");

  const clinicName =
    config.hospital_name ||
    doctor?.hospital ||
    doctor?.organization_name ||
    auth?.organization_name ||
    "";

  const clinicSlogan = real(config.slogan);

  const clinicAddress =
    real(config.address) ||
    real(doctor?.address) ||
    real(doctor?.organization_address) ||
    "";

  const clinicPhone =
    real(config.phone) ||
    real(doctor?.phone_number) ||
    real(doctor?.organization_phone) ||
    real(doctor?.user?.phone_number) ||
    "";

  const clinicEmail =
    real(config.email) ||
    real(doctor?.email) ||
    real(doctor?.user?.email) ||
    real(auth?.email) ||
    "";

  const clinicWebsite =
    real(config.website) ||
    real(doctor?.website) ||
    "";

  const patientName =
    patient?.name ||
    patient?.fullname ||
    patient?.patient_name ||
    consultationData?.patient_name ||
    consultationData?.patients?.name ||
    "Patient";

  // Always print age only (never a raw DOB) — works the same whether the patient
  // has an exact date of birth or only an age-derived (Jan 1 placeholder) one.
  // Computed as of the CONSULTATION's own date, not whenever this prescription
  // happens to be printed - reprinting a 2-year-old consultation today must
  // still show the age the patient was on that date, not their current age.
  const asOfDateSource = consultationData?.sessiondate || doctor?.date;
  const asOfDate = asOfDateSource ? new Date(asOfDateSource) : new Date();

  const calculateAgeFromDob = (dobStr) => {
    if (!dobStr) return null;
    const dobDate = new Date(dobStr);
    if (isNaN(dobDate.getTime()) || isNaN(asOfDate.getTime())) return null;
    let age = asOfDate.getFullYear() - dobDate.getFullYear();
    const m = asOfDate.getMonth() - dobDate.getMonth();
    if (m < 0 || (m === 0 && asOfDate.getDate() < dobDate.getDate())) age--;
    return age >= 0 ? age : null;
  };

  const dobSource = patient?.dob || consultationData?.patients?.dob;
  const computedAge = calculateAgeFromDob(dobSource);
  const rawAge = computedAge !== null ? computedAge : (patient?.age || patient?.patient_age || consultationData?.patients?.age_group || "");
  const patientAge = rawAge !== "" && rawAge !== null ? (String(rawAge).toLowerCase().includes("yr") ? rawAge : `${rawAge} Yrs`) : "";
  const patientGender = patient?.gender || patient?.patient_gender || consultationData?.patients?.gender || "";

  // Always "21 Oct 2026" on the printout; a stored ISO date is reformatted.
  const printDate = (v) => {
    const dt = v ? new Date(/^\d{4}-\d{2}-\d{2}$/.test(v) ? `${v}T00:00:00` : v) : new Date();
    return isNaN(dt) ? String(v) : dt.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
  };
  const consultationDate = printDate(consultationData?.sessiondate || doctor?.date);

  // Parse medicines from gp_prescription string or array
  let medicines = [];
  const rawPrescription = consultationData?.gp_prescription || consultationData?.prescription || "";
  if (Array.isArray(rawPrescription)) {
    medicines = rawPrescription.map((m, idx) => ({
      index: idx + 1,
      name: m.name || m.drug_name || "",
      food: m.food || m.timing || "After Food",
      dose: m.dose || m.dosage || "1-0-1",
      duration: m.duration || "5 Days"
    }));
  } else if (typeof rawPrescription === "string" && rawPrescription.trim()) {
    const medLines = rawPrescription
      .split("\n")
      .map((l) => l.trim())
      .filter(Boolean);

    medicines = medLines.map((line, idx) => {
      const cleanLine = line.replace(/^\d+[\.\)]\s*|^-\s*/, "");
      const parts = cleanLine.split("|").map((p) => p.trim());
      if (parts.length >= 2) {
        return {
          index: idx + 1,
          name: parts[0] || line,
          food: parts[1] || "After Food",
          dose: parts[2] || "1-0-1",
          duration: parts[3] || "5 Days"
        };
      } else {
        return {
          index: idx + 1,
          name: cleanLine,
          food: "As Directed",
          dose: "As Prescribed",
          duration: "As Advised"
        };
      }
    });
  }

  // Parse diagnostic tests/investigations from gp_tests (same pipe-delimited
  // shape as gp_prescription: "1. Test Name | reason/notes")
  let tests = [];
  const rawTests = consultationData?.gp_tests || "";
  if (typeof rawTests === "string" && rawTests.trim()) {
    tests = rawTests
      .split("\n")
      .map((l) => l.trim())
      .filter(Boolean)
      .map((line) => {
        const cleanLine = line.replace(/^\d+[\.\)]\s*|^-\s*/, "");
        const parts = cleanLine.split("|").map((p) => p.trim());
        return { name: parts[0] || cleanLine, notes: parts[1] || "" };
      });
  }

  const isMentalHealth = consultationData?.consultation_type === "mental_health";
  const isVisitSummary = isMentalHealth && !canPrescribeMedicine;
  
  const diagnosis = isVisitSummary && !showDiagnosis ? "" : (consultationData?.gp_diagnosis || consultationData?.diagnosis || "");
  // Shown instead when there's no diagnosis: CBT/REBT/SFBT is a method, not a diagnosis.
  const therapyApproach = !diagnosis && isMentalHealth ? (consultationData?.therapy_type || "") : "";

  // Patient-facing fields only. The mental-health clinical note (themes,
  // mood, summary, outcome, interventions, private plan, AI feedback) is the
  // clinician's own and is never printed on the patient's copy.
  const symptoms = isVisitSummary ? "" : (consultationData?.gp_symptoms || consultationData?.symptoms || "");

  const vitals = isVisitSummary ? "" : (consultationData?.gp_vitals || consultationData?.vitals || "");

  const adviceParts = [];
  if (consultationData?.gp_advice) adviceParts.push(consultationData.gp_advice);
  else if (consultationData?.advice) adviceParts.push(consultationData.advice);

  const advice = adviceParts.join("\n\n");

  const esc = (v) => String(v ?? "").replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c]));
  const fmtDay = (d) => {
    const dt = new Date(`${d}T00:00:00`);
    if (isNaN(dt)) return d;
    const part = (o) => dt.toLocaleDateString("en-IN", o);
    return `${part({ weekday: "short" })} ${part({ day: "numeric" })} ${part({ month: "short" })} ${part({ year: "numeric" })}`;
  };
  const fmtTime = (t) => {
    if (!t) return "";
    const [h, m] = t.split(":").map(Number);
    return `${((h + 11) % 12) + 1}:${String(m).padStart(2, "0")} ${h < 12 ? "AM" : "PM"}`;
  };

  let visitSummaryHtml = "";
  if (isVisitSummary) {
    const vs = visitSummary || {};
    const summaryText = advice || consultationData?.patient_summary || "";
    const meta = [vs.session_number ? `Session ${vs.session_number}` : "", vs.mode || ""].filter(Boolean).join(" · ");
    const homework = vs.homework || [];
    const goals = vs.goals || [];
    const next = vs.next_visit;
    visitSummaryHtml = `
    ${meta ? `<div class="vs-meta">${esc(meta)}</div>` : ""}
    ${summaryText ? `
    <div class="advice-container">
      <div class="advice-title">From today's session</div>
      <div class="advice-body">${esc(summaryText)}</div>
    </div>` : ""}
    ${homework.length ? `
    <div class="advice-container">
      <div class="advice-title">To work on before next time</div>
      <div class="advice-body">${homework.map((h) => `• ${esc(h.title)}${h.due_date ? ` <span class="vs-muted">(by ${esc(fmtDay(h.due_date))})</span>` : ""}`).join("<br/>")}</div>
      <div class="vs-muted" style="margin-top:4px;">Open these in My Care on the booking app.</div>
    </div>` : ""}
    ${goals.length ? `
    <div class="advice-container">
      <div class="advice-title">Goals we're working on${vs.session_frequency ? ` <span class="vs-muted" style="text-transform:none;font-weight:600;">· meeting ${esc(vs.session_frequency.toLowerCase())}</span>` : ""}</div>
      <div class="advice-body">${goals.map((g) => `${g.status === "achieved" ? "✓" : "•"} ${esc(g.goal)}${g.status === "achieved" ? ` <span class="vs-muted">(reached)</span>` : ""}`).join("<br/>")}</div>
    </div>` : ""}
    ${next ? `
    <div class="vs-next"><strong>Your next visit:</strong> ${esc(fmtDay(next.date))}${next.time ? `, ${esc(fmtTime(next.time))}` : ""}${next.mode ? ` · ${esc(next.mode)}` : ""}${next.branch_name ? ` · ${esc(next.branch_name)}` : ""}</div>` : ""}
    <div class="vs-muted" style="margin-top:6px;">If you ever feel unsafe, call Tele-MANAS 14416 (free, 24x7) or 112.</div>
    `;
  }

  // Check custom logo vs default stethoscope
  // An S3-stored logo is saved as "s3://<key>" - only the backend's fresh
  // logo_display_url can be loaded (organization/letterhead_logo.py).
  const logoSrc = config.logo_display_url ||
    (config.logo_url && !String(config.logo_url).startsWith("s3://") ? config.logo_url : "");
  const isCustomLogo = config.logo_type === "custom" && Boolean(logoSrc);

  const printHtml = `
<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <title>${isVisitSummary ? "Visit Summary" : "Prescription"} - ${patientName}</title>
  <style>
    @page {
      size: A4 portrait;
      margin: 0;
    }
    *, *::before, *::after {
      box-sizing: border-box;
      margin: 0;
      padding: 0;
      -webkit-print-color-adjust: exact !important;
      print-color-adjust: exact !important;
      color-adjust: exact !important;
    }
    html, body {
      font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
      color: #1e293b;
      background: #ffffff;
      width: 210mm;
      height: 297mm;
      margin: 0;
      padding: 0;
      -webkit-font-smoothing: antialiased;
      -webkit-print-color-adjust: exact !important;
      print-color-adjust: exact !important;
    }
    .page-sheet {
      width: 210mm;
      height: 297mm;
      min-height: 297mm;
      max-height: 297mm;
      padding: ${hideHeader ? "10mm 18mm 44mm 18mm" : "12mm 18mm 44mm 18mm"};
      margin: 0 auto;
      background: #ffffff;
      position: relative;
      display: flex;
      flex-direction: column;
      box-sizing: border-box;
      overflow: hidden;
    }

    /* Top Letterhead Table */
    .header-table {
      width: 100%;
      border-collapse: collapse;
      margin-bottom: 6px;
    }
    .header-left {
      vertical-align: top;
      padding-right: 20px;
    }
    .header-right {
      vertical-align: top;
      text-align: right;
      width: 220px;
      min-width: 190px;
    }
    .hospital-title {
      font-size: 24px;
      font-weight: 800;
      color: ${themePalette.primary};
      letter-spacing: -0.02em;
      line-height: 1.15;
      margin-bottom: 2px;
      text-transform: capitalize;
    }
    .hospital-slogan {
      font-size: 11.5px;
      color: #64748b;
      font-weight: 500;
      margin-bottom: 5px;
    }
    .doctor-meta-line {
      font-size: 13px;
      font-weight: 700;
      color: #0f172a;
      margin-bottom: 1px;
      line-height: 1.25;
    }
    .doctor-specialization-line {
      font-size: 10.5px;
      font-weight: 700;
      color: ${themePalette.primary};
      text-transform: none;
      letter-spacing: 0.03em;
      margin-bottom: 2px;
      line-height: 1.3;
    }
    .doctor-qualifications {
      font-size: 10.5px;
      font-weight: 600;
      color: #334155;
      line-height: 1.35;
      margin-bottom: 3px;
    }
    .doctor-qual-line {
      margin-bottom: 1px;
    }
    .doctor-reg-line {
      font-size: 9.5px;
      color: #64748b;
      font-weight: normal;
      margin-top: 1px;
    }
    .hospital-address {
      font-size: 10.5px;
      color: #475569;
      line-height: 1.35;
      max-width: 440px;
    }
    .logo-container {
      display: inline-block;
      text-align: right;
      margin-bottom: 3px;
    }
    .custom-logo-img {
      max-height: 60px;
      max-width: 130px;
      object-fit: contain;
    }
    .header-contact-box {
      text-align: right;
      font-size: 9.5px;
      color: #64748b;
      line-height: 1.4;
      border-top: 1px solid ${themePalette.primaryBorder};
      padding-top: 3px;
      margin-top: 2px;
    }
    .contact-line {
      white-space: nowrap;
    }
    .contact-label {
      font-weight: 700;
      color: #475569;
    }

    /* Pre-printed Pad Top Spacer (when header is hidden) */
    .preprinted-pad-spacer {
      height: 48mm;
      width: 100%;
      display: block;
    }
    .preprinted-indicator {
      font-size: 9px;
      color: #cbd5e1;
      text-align: center;
      letter-spacing: 0.08em;
      text-transform: uppercase;
      padding-top: 4px;
    }

    /* Patient Details Header Line (Dotted classical pad style) */
    .patient-line-container {
      display: flex;
      align-items: baseline;
      gap: 16px;
      margin-top: ${hideHeader ? "0" : "6px"};
      margin-bottom: 8px;
      padding-bottom: 5px;
      font-size: 13.5px;
      color: #1e293b;
      font-family: inherit;
      width: 100%;
    }
    .p-name-block {
      flex: 1;
      display: flex;
      align-items: baseline;
      overflow: hidden;
      white-space: nowrap;
    }
    .p-label {
      font-weight: 700;
      color: #0f172a;
      margin-right: 6px;
    }
    .p-val {
      font-weight: 600;
      color: #1e293b;
      margin-right: 4px;
    }
    .p-dots {
      flex: 1;
      overflow: hidden;
      color: #94a3b8;
      font-size: 14px;
      letter-spacing: 2px;
    }
    .p-item {
      white-space: nowrap;
    }

    /* Rx Symbol Header */
    .rx-header-row {
      display: flex;
      align-items: center;
      justify-content: space-between;
      margin-top: 4px;
      margin-bottom: 12px;
    }
    .rx-symbol {
      font-family: Georgia, "Times New Roman", serif;
      font-size: 40px;
      font-weight: bold;
      font-style: italic;
      color: ${themePalette.primary};
      line-height: 1;
      display: inline-block;
    }
    /* Non-prescribing specializations (psychologist, counselor, etc.) get
       this instead of the Rx glyph, which specifically implies a medicine
       order - printing it on a therapy note with no medicines is misleading. */
    .note-symbol {
      font-size: 13px;
      font-weight: 800;
      text-transform: uppercase;
      letter-spacing: 0.06em;
      color: ${themePalette.primary};
      display: inline-block;
    }
    .diagnosis-pill {
      background: ${themePalette.primaryLight};
      border: 1px solid ${themePalette.primaryBorder};
      border-left: 4px solid ${themePalette.primary};
      padding: 5px 12px;
      border-radius: 6px;
      display: inline-flex;
      align-items: baseline;
      gap: 6px;
    }
    .diag-label {
      font-size: 10px;
      font-weight: 800;
      letter-spacing: 0.05em;
      color: ${themePalette.primaryDark};
      text-transform: uppercase;
    }
    .diag-val {
      font-size: 12.5px;
      font-weight: 700;
      color: ${themePalette.primaryDark};
      text-transform: capitalize;
    }

    /* Clinical Vitals & Complaints (Compact) */
    .clinical-cards-grid {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 10px;
      margin-bottom: 14px;
    }
    .c-card {
      background: #fafaf9;
      border: 1px solid #e7e5e4;
      border-radius: 6px;
      padding: 6px 10px;
    }
    .c-title {
      font-size: 9.5px;
      font-weight: 800;
      text-transform: uppercase;
      letter-spacing: 0.05em;
      color: ${themePalette.primary};
      margin-bottom: 3px;
    }
    .c-text {
      font-size: 11.5px;
      color: #334155;
      line-height: 1.35;
      white-space: pre-wrap;
    }

    /* Prescription Table (When Medicines Exist) */
    table.rx-table {
      width: 100%;
      border-collapse: collapse;
      margin-top: 6px;
      margin-bottom: 16px;
    }
    table.rx-table th {
      background: ${themePalette.primaryLight};
      border-top: 1.5px solid ${themePalette.primary};
      border-bottom: 1.5px solid ${themePalette.primary};
      color: ${themePalette.primaryDark};
      font-size: 11px;
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: 0.04em;
      padding: 8px 10px;
      text-align: left;
    }
    table.rx-table td {
      padding: 9px 10px;
      border-bottom: 1px solid #f1f5f9;
      font-size: 12px;
      color: #1e293b;
      vertical-align: middle;
    }
    table.rx-table tr:nth-child(even) {
      background-color: #fcfcfc;
    }
    .dose-tag {
      display: inline-block;
      background: #f1f5f9;
      color: #0f172a;
      font-weight: 700;
      padding: 2px 7px;
      border-radius: 4px;
      font-size: 11px;
    }

    /* Blank Pad Lines (when no digital medicines entered yet) */
    .blank-pad-area {
      margin-top: 10px;
      margin-bottom: 15px;
    }
    .pad-line {
      height: 32px;
      border-bottom: 1px dotted #e2e8f0;
      width: 100%;
    }

    /* Advice box */
    .vs-meta {
      font-size: 11px;
      font-weight: 700;
      color: #475569;
      margin: 2px 0 8px 0;
    }
    .vs-muted {
      font-size: 10px;
      color: #64748b;
    }
    .vs-next {
      font-size: 12px;
      color: #0f172a;
      border-left: 3px solid ${themePalette.primary};
      padding: 6px 10px;
      background: #f8fafc;
      border-radius: 4px;
    }
    .advice-container {
      background: #f8fafc;
      border: 1px dashed #cbd5e1;
      border-radius: 6px;
      padding: 10px 14px;
      margin-top: 10px;
      margin-bottom: 16px;
    }
    .advice-title {
      font-size: 10.5px;
      font-weight: 800;
      text-transform: uppercase;
      letter-spacing: 0.04em;
      color: #475569;
      margin-bottom: 4px;
    }
    .advice-body {
      font-size: 11.5px;
      color: #334155;
      line-height: 1.4;
      white-space: pre-wrap;
    }

    /* Tests / Investigations box - visually distinct from General Advice
       below it (purple accent vs the neutral slate advice box) so the two
       don't blur together on the printed page. */
    .tests-container {
      background: #faf5ff;
      border: 1px solid #e9d5ff;
      border-left: 3px solid #9333ea;
      border-radius: 6px;
      padding: 10px 14px;
      margin-top: 10px;
      margin-bottom: 10px;
    }
    .tests-title {
      font-size: 10.5px;
      font-weight: 800;
      text-transform: uppercase;
      letter-spacing: 0.04em;
      color: #7e22ce;
      margin-bottom: 4px;
    }

    /* Bottom Signature Area - Absolute pinned to bottom of page */
    .bottom-section {
      position: absolute;
      bottom: ${hideHeader ? "14mm" : "11mm"};
      left: 18mm;
      right: 18mm;
      padding-top: 0;
    }
    .signature-row {
      display: flex;
      justify-content: space-between;
      align-items: flex-end;
      margin-bottom: ${hideHeader ? "0" : "6px"};
    }
    .seal-block {
      max-width: 55%;
    }
    .seal-badge {
      display: inline-flex;
      align-items: center;
      gap: 5px;
      font-size: 10px;
      font-weight: 700;
      color: ${themePalette.primary};
      text-transform: uppercase;
      letter-spacing: 0.04em;
      margin-bottom: 3px;
    }
    .seal-text {
      font-size: 9.5px;
      color: #94a3b8;
      line-height: 1.3;
    }
    .doctor-signature-box {
      text-align: right;
      min-width: 200px;
    }
    .sig-line {
      width: 180px;
      border-top: 1.5px solid #334155;
      margin-left: auto;
      margin-bottom: 6px;
    }
    .sig-doctor {
      font-size: 13px;
      font-weight: 800;
      color: #0f172a;
    }
    .sig-specialization {
      font-size: 10.5px;
      font-weight: 700;
      color: ${themePalette.primary};
      text-transform: none;
      letter-spacing: 0.03em;
      margin-top: 1px;
      margin-bottom: 1px;
    }
    .sig-details {
      font-size: 10px;
      color: #475569;
      margin-top: 1px;
      line-height: 1.35;
    }
    .sig-qual-line {
      margin-bottom: 1px;
      line-height: 1.3;
    }
    .sig-sub {
      font-size: 9px;
      font-weight: 700;
      color: #64748b;
      text-transform: uppercase;
      letter-spacing: 0.04em;
      margin-top: 3px;
    }

    /* Edge-to-Edge Bottom Stripes */
    .footer-stripes {
      position: absolute;
      bottom: 0;
      left: 0;
      right: 0;
      width: 100%;
      overflow: hidden;
    }
    .stripe-primary {
      background-color: ${themePalette.stripe1};
      height: 8px;
      width: 100%;
    }
    .stripe-secondary {
      background-color: ${themePalette.stripe2};
      height: 18px;
      width: 100%;
    }

    @media print {
      @page {
        size: A4 portrait;
        margin: 0;
      }
      html, body {
        width: 210mm !important;
        height: 297mm !important;
        margin: 0 !important;
        padding: 0 !important;
      }
      .page-sheet {
        width: 210mm !important;
        height: 297mm !important;
        min-height: 297mm !important;
        max-height: 297mm !important;
        padding: ${hideHeader ? "10mm 18mm 44mm 18mm !important" : "12mm 18mm 44mm 18mm !important"};
        margin: 0 !important;
        position: relative !important;
        overflow: hidden !important;
        page-break-inside: avoid !important;
        page-break-after: avoid !important;
      }
      .bottom-section {
        position: absolute !important;
        bottom: ${hideHeader ? "14mm !important" : "11mm !important"};
        left: 18mm !important;
        right: 18mm !important;
      }
      .footer-stripes {
        position: absolute !important;
        bottom: 0 !important;
        left: 0 !important;
        right: 0 !important;
      }
    }
  </style>
</head>
<body>
  <div class="page-sheet">
    ${hideHeader
      ? `
        <!-- Pre-printed Stationery Space -->
        <div class="preprinted-pad-spacer">
          <div class="preprinted-indicator">▼ Printed on Pre-printed Hospital Letterhead Pad ▼</div>
        </div>
        `
      : `
        <!-- Top Header Table -->
        <table class="header-table">
          <tr>
            <td class="header-left">
              <div class="hospital-title">${clinicName}</div>
              ${clinicSlogan ? `<div class="hospital-slogan">${clinicSlogan}</div>` : ""}
              <div class="doctor-meta-line">
                ${doctorName}
              </div>
              ${doctorSpecialization ? `<div class="doctor-specialization-line">${doctorSpecialization}</div>` : ""}
              <div class="doctor-qualifications">
                ${degreeLines.length > 1
        ? degreeLines.map((line) => `<div class="doctor-qual-line">${line}</div>`).join("")
        : (degreeLines.length === 1 ? `<div class="doctor-qual-line">${degreeLines[0]}</div>` : "")
      }
                ${doctorReg ? `<div class="doctor-reg-line">Reg: ${doctorReg}</div>` : ""}
              </div>
              <div class="hospital-address">
                ${clinicAddress}
              </div>
            </td>
            <td class="header-right">
              <div class="logo-container">
                ${isCustomLogo
        ? `<img src="${logoSrc}" class="custom-logo-img" alt="Clinic Logo" />`
        : `
                    <!-- Medical Stethoscope + Heart Vector Logo -->
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 120 120" width="68" height="68">
                      <!-- Heart Shape (Right Side) -->
                      <path d="M68 106 C68 106, 114 80, 114 48 C114 30, 99 18, 83 18 C72 18, 66 25, 64 28 C62 25, 56 18, 45 18 C33 18, 22 28, 22 45 C22 55, 27 67, 35 78 C31 75, 27 70, 25 64 C23 72, 28 82, 36 89 C45 97, 57 103, 68 106 Z" fill="${themePalette.primary}" />
                      <!-- Stethoscope Chestpiece on Heart -->
                      <circle cx="75" cy="70" r="14" fill="#ffffff" />
                      <circle cx="75" cy="70" r="7" fill="${themePalette.primary}" />
                      <!-- Stethoscope Flexible Tube looping around -->
                      <path d="M75 84 C75 98, 48 108, 32 94 C18 82, 14 62, 18 45 C22 30, 32 20, 36 18" stroke="${themePalette.primary}" stroke-width="5" stroke-linecap="round" fill="none" />
                      <!-- Binaural Tubes and Ear Tips -->
                      <path d="M22 28 C16 20, 16 10, 20 4" stroke="${themePalette.primary}" stroke-width="4.5" stroke-linecap="round" fill="none" />
                      <path d="M34 26 C36 18, 36 10, 32 4" stroke="${themePalette.primary}" stroke-width="4.5" stroke-linecap="round" fill="none" />
                      <circle cx="20" cy="4" r="3.5" fill="${themePalette.primary}" />
                      <circle cx="32" cy="4" r="3.5" fill="${themePalette.primary}" />
                    </svg>
                    `
      }
              </div>
              <div class="header-contact-box">
                ${clinicPhone ? `<div class="contact-line"><span class="contact-label">Ph:</span> ${clinicPhone}</div>` : ""}
                ${clinicEmail ? `<div class="contact-line"><span class="contact-label">Email:</span> ${clinicEmail}</div>` : ""}
                ${clinicWebsite ? `<div class="contact-line"><span class="contact-label">Web:</span> ${clinicWebsite}</div>` : ""}
                ${config.gstin ? `<div class="contact-line"><span class="contact-label">GSTIN:</span> ${config.gstin}</div>` : ""}
              </div>
            </td>
          </tr>
        </table>
        `
    }

    <!-- Patient Details Line (Classic Medical Pad Style) -->
    <div class="patient-line-container">
      <div class="p-name-block">
        <span class="p-label">Name :</span>
        <span class="p-val">${patientName}</span>
        <span class="p-dots">....................................................................................................................................</span>
      </div>
      <div class="p-item">
        <span class="p-label">Age :</span>
        <span class="p-val">${patientAge || "____"}</span>
      </div>
      <div class="p-item">
        <span class="p-label">Sex :</span>
        <span class="p-val">${patientGender || "____"}</span>
      </div>
      <div class="p-item">
        <span class="p-label">Date :</span>
        <span class="p-val">${consultationDate}</span>
      </div>
    </div>

    <!-- Rx Row (Icon + Diagnosis) -->
    <div class="rx-header-row">
      ${canPrescribeMedicine
      ? `<span class="rx-symbol">℞</span>`
      : `<span class="note-symbol">${isVisitSummary ? "Visit Summary" : "Consultation Note"}</span>`
    }
      ${diagnosis || therapyApproach
      ? `
      <div class="diagnosis-pill">
        <span class="diag-label">${diagnosis ? "PROVISIONAL DIAGNOSIS:" : "THERAPY APPROACH:"}</span>
        <span class="diag-val">${diagnosis || therapyApproach}</span>
      </div>
      `
      : ""
    }
    </div>

    <!-- Clinical Findings: Complaints & Vitals (if recorded) -->
    ${symptoms || vitals
      ? `
    <div class="clinical-cards-grid">
      ${symptoms
        ? `
      <div class="c-card">
        <div class="c-title">Chief Complaints & History</div>
        <div class="c-text">${symptoms}</div>
      </div>
      `
        : `<div></div>`
      }
      ${vitals
        ? `
      <div class="c-card">
        <div class="c-title">Vitals & Physical Exam</div>
        <div class="c-text">${vitals}</div>
      </div>
      `
        : `<div></div>`
      }
    </div>
    `
      : ""
    }

    <!-- Prescription Medications Table (if medicines prescribed) -->
    ${medicines.length > 0
      ? `
    <table class="rx-table">
      <thead>
        <tr>
          <th style="width: 35px; text-align: center;">#</th>
          <th>Medicine Name & Strength</th>
          <th style="width: 140px;">Timing (Food)</th>
          <th style="width: 140px;">Dosage Frequency</th>
          <th style="width: 100px;">Duration</th>
        </tr>
      </thead>
      <tbody>
        ${medicines
        .map(
          (m) => `
          <tr>
            <td style="text-align: center; color: #64748b; font-weight: 600;">${m.index}</td>
            <td><strong style="color: #0f172a; font-size: 12.5px;">${m.name}</strong></td>
            <td>${m.food}</td>
            <td><span class="dose-tag">${m.dose}</span></td>
            <td>${m.duration}</td>
          </tr>
        `
        )
        .join("")}
      </tbody>
    </table>
    `
      : !canPrescribeMedicine ? "" : `
    <!-- Blank Ruled Pad Lines for writing a prescription by hand -->
    <div class="blank-pad-area">
      <div class="pad-line"></div>
      <div class="pad-line"></div>
      <div class="pad-line"></div>
      <div class="pad-line"></div>
      <div class="pad-line"></div>
      <div class="pad-line"></div>
      <div class="pad-line"></div>
      <div class="pad-line"></div>
    </div>
    `
    }

    <!-- Tests / Investigations Advised (if recorded) -->
    ${tests.length > 0
      ? `
    <div class="tests-container">
      <div class="tests-title">Tests / Investigations Advised:</div>
      <div class="advice-body">${tests
        .map((t) => `• <strong>${t.name}</strong>${t.notes ? ` (${t.notes})` : ""}`)
        .join("<br/>")}</div>
    </div>
    `
      : ""
    }

    ${visitSummaryHtml}

    <!-- General Advice & Follow-up (if recorded) -->
    ${!isVisitSummary && advice
      ? `
    <div class="advice-container">
      <div class="advice-title">General Advice & Instructions:</div>
      <div class="advice-body">${advice}</div>
    </div>
    `
      : ""
    }

    <!-- Bottom Section: Signature Block & Legal Seal -->
    <div class="bottom-section">
      <div class="signature-row">
        <div class="seal-block">
          <div class="seal-badge">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="${themePalette.primary}" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
            </svg>
            <span>${canPrescribeMedicine ? "Electronically Validated Prescription" : isVisitSummary ? "Electronically Generated Visit Summary" : "Electronically Generated Consultation Note"}</span>
          </div>
          <div class="seal-text">
            ${clinicName ? `Generated via ${clinicName}.` : ""}${canPrescribeMedicine ? " Valid for pharmacy dispensing under Indian Telemedicine & Clinical Practice Guidelines." : ""}
          </div>
        </div>

        <div class="doctor-signature-box">
          <div class="sig-line"></div>
          <div class="sig-doctor">${doctorName}</div>
          ${doctorSpecialization ? `<div class="sig-specialization">${doctorSpecialization}</div>` : ""}
          <div class="sig-details">
            ${degreeLines.length > 1
      ? degreeLines.map((line) => `<div class="sig-qual-line">${line}</div>`).join("")
      : (degreeLines.length === 1 ? `<div>${degreeLines[0]}</div>` : "")
    }
            ${doctorReg ? `<div style="font-size: 9.5px; color: #64748b; margin-top: 1.5px;">Reg: ${doctorReg}</div>` : ""}
          </div>
          <div class="sig-sub">(Authorized Signatory)</div>
        </div>
      </div>
    </div>

    ${hideHeader
      ? ""
      : `
        <!-- Dual Color Bottom Stripes -->
        <div class="footer-stripes">
          <div class="stripe-primary"></div>
          <div class="stripe-secondary"></div>
        </div>
        `
    }
  </div>

  ${triggerPrint
      ? `
  <script>
    window.onload = function() {
      setTimeout(function() {
        window.print();
      }, 250);
    };
  </script>
  `
      : ""
    }
</body>
</html>
  `.trim();

  return printHtml;
}

export function printMedicalPrescription(options) {
  const printHtml = buildPrescriptionHtml({ ...options, triggerPrint: true });

  // Print using a hidden iframe to prevent popup blockers
  const iframe = document.createElement("iframe");
  iframe.style.position = "fixed";
  iframe.style.right = "0";
  iframe.style.bottom = "0";
  iframe.style.width = "0";
  iframe.style.height = "0";
  iframe.style.border = "0";
  iframe.id = "prescription-print-frame";

  document.body.appendChild(iframe);

  const doc = iframe.contentWindow.document;
  doc.open();
  doc.write(printHtml);
  doc.close();

  // Clean up after print dialogue closes
  setTimeout(() => {
    try {
      if (document.body.contains(iframe)) {
        document.body.removeChild(iframe);
      }
    } catch (e) {
      // Ignore
    }
  }, 60000);
}
